import { useEffect, useState } from "react";
import { Loader2, Check, X, Ticket } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

const STATUS_CLS = {
  pending: "text-amber-300 border-amber-500/40",
  approved: "text-emerald-300 border-emerald-500/40",
  rejected: "text-red-300 border-red-500/40",
};

export function DiscountRequestsTable() {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = () =>
    api.get("/admin/discount-requests").then(({ data }) => setRows(data)).catch(() => {}).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const review = async (id, status) => {
    setBusy(id);
    try {
      const { data } = await api.patch(`/admin/discount-requests/${id}`, { status });
      setRows((arr) => arr.map((r) => (r.id === id ? data : r)));
      toast.success(status === "approved" ? t("dcodes.approved") : t("dcodes.rejected"));
    } catch {
      toast.error("Error");
    } finally {
      setBusy(null);
    }
  };

  const pending = rows.filter((r) => r.status === "pending").length;

  return (
    <div data-testid="dcodes-tab">
      <div className="grid grid-cols-1 gap-4 mb-6 max-w-xs">
        <div className="rounded-xl border border-aura-border bg-aura-surface p-5">
          <div className="flex items-center justify-between mb-2"><span className="text-xs uppercase tracking-[0.1em] text-zinc-500">{t("dcodes.pendingCount")}</span><Ticket size={15} className="text-zinc-600" /></div>
          <div className="text-2xl font-display font-bold text-white">{pending}</div>
        </div>
      </div>

      <div className="rounded-xl border border-aura-border bg-aura-surface overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-start text-xs uppercase tracking-wider text-zinc-500">
                <th className="px-4 py-3 font-medium text-start">{t("dcodes.colCreator")}</th>
                <th className="px-4 py-3 font-medium text-start">{t("dcodes.colCode")}</th>
                <th className="px-4 py-3 font-medium text-start">{t("dcodes.colPercent")}</th>
                <th className="px-4 py-3 font-medium text-start">{t("dcodes.colUses")}</th>
                <th className="px-4 py-3 font-medium text-start">{t("dcodes.colSales")}</th>
                <th className="px-4 py-3 font-medium text-start">{t("dcodes.colCommission")}</th>
                <th className="px-4 py-3 font-medium text-start">{t("dcodes.colStatus")}</th>
                <th className="px-4 py-3 font-medium text-end"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-16 text-center text-zinc-500"><Loader2 className="animate-spin inline" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-16 text-center text-zinc-500">{t("dcodes.empty")}</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} data-testid={`dcode-row-${r.id}`} className="border-b border-white/5">
                  <td className="px-4 py-4 text-white">{r.creator_name}</td>
                  <td className="px-4 py-4 font-mono text-white">{r.code}</td>
                  <td className="px-4 py-4 text-zinc-300">{r.percent}%</td>
                  <td className="px-4 py-4 text-zinc-300">{r.times_used || 0}</td>
                  <td className="px-4 py-4 text-zinc-300">€{(r.total_sales || 0).toLocaleString()}</td>
                  <td className="px-4 py-4 text-zinc-300">€{(r.total_commission || 0).toLocaleString()}</td>
                  <td className="px-4 py-4"><span className={`inline-flex items-center rounded-full border ${STATUS_CLS[r.status]} bg-white/[0.03] px-3 py-1 text-xs font-medium`}>{t(`dcodes.${r.status}`)}</span></td>
                  <td className="px-4 py-4">
                    {r.status === "pending" && (
                      <div className="flex items-center justify-end gap-2">
                        <button data-testid={`dcode-approve-${r.id}`} disabled={busy === r.id} onClick={() => review(r.id, "approved")}
                          className="inline-flex items-center gap-1 rounded-lg border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50">
                          <Check size={13} /> {t("dcodes.approve")}
                        </button>
                        <button data-testid={`dcode-reject-${r.id}`} disabled={busy === r.id} onClick={() => review(r.id, "rejected")}
                          className="inline-flex items-center gap-1 rounded-lg border border-red-500/40 text-red-300 hover:bg-red-500/10 px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50">
                          <X size={13} /> {t("dcodes.reject")}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
